/**
 * KernelList — List of Kernel Strategy entries
 *
 * Mirrors the StrategyList pattern using CollectionView.
 * Route: /prioritization/strategy/kernel
 */

import { useMemo, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LightBulbIcon } from '@heroicons/react/24/outline';
import { useStrategyKernelStore } from '../../../../stores/prioritization/strategyKernelStore';
import { StrategyConnectivityHelpButton } from '../StrategyConnectivityDocModal';
import { EntityListPage } from '../../../../components/collection';
import type { FacetDefinition, SortOption } from '../../../../components/collection/types';
import type { StrategyKernel } from '../../../../types/prioritization/strategyKernel';
import {
  getKernelStatusLabel,
  getKernelTimeHorizonLabel,
} from '../../../../types/prioritization/strategyKernel';

// ============================================================================
// Row renderer
// ============================================================================

function KernelRow({ kernel }: { kernel: StrategyKernel }) {
  const diagnosisPreview = kernel.diagnosis
    ? kernel.diagnosis.slice(0, 100) + (kernel.diagnosis.length > 100 ? '…' : '')
    : null;

  return (
    <Link
      to={`/prioritization/strategy/kernel/${kernel.id}`}
      className="flex items-start px-4 py-4 hover:bg-[var(--surface)] transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[var(--text-pri)] truncate">{kernel.name}</span>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            kernel.status === 'active'   ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
            kernel.status === 'draft'    ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'  :
                                           'bg-[var(--coral-dim)] text-[var(--coral)]'
          }`}>
            {getKernelStatusLabel(kernel.status)}
          </span>
          <span className="inline-flex items-center rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs font-medium text-[var(--text-sec)]">
            {getKernelTimeHorizonLabel(kernel.timeHorizon)}
          </span>
          {kernel.cruxId && (
            <span className="inline-flex items-center rounded-full bg-[var(--mustard-dim)] px-2 py-0.5 text-xs font-medium text-[var(--mustard)]">
              Crux set
            </span>
          )}
        </div>
        {diagnosisPreview && (
          <p className="mt-0.5 text-sm text-[var(--text-ter)] truncate">{diagnosisPreview}</p>
        )}
        {!diagnosisPreview && (
          <p className="mt-0.5 text-xs text-[var(--text-ter)] italic">Diagnosis not yet written</p>
        )}
      </div>
      <div className="ml-4 flex-shrink-0 text-right text-xs text-[var(--text-ter)] space-y-0.5">
        <div>{kernel.coherentActions.length} actions</div>
        <div>{kernel.linkedCycleIds.length} cycles</div>
      </div>
    </Link>
  );
}

// ============================================================================
// Facets / sorts
// ============================================================================

const FACETS: FacetDefinition<string>[] = [
  {
    field: 'status',
    label: 'Status',
    type: 'multi',
    options: [
      { value: 'active',   label: 'Active' },
      { value: 'draft',    label: 'Draft' },
      { value: 'archived', label: 'Archived' },
    ],
  },
  {
    field: 'timeHorizon',
    label: 'Horizon',
    type: 'multi',
    options: [
      { value: 'quarterly',  label: 'Quarterly' },
      { value: 'annual',     label: 'Annual' },
      { value: 'multi-year', label: 'Multi-Year' },
    ],
  },
];

const SORT_OPTIONS: SortOption<string>[] = [
  { field: 'name',      label: 'Name' },
  { field: 'status',    label: 'Status' },
  { field: 'updatedAt', label: 'Last Updated' },
  { field: 'createdAt', label: 'Created' },
];

// ============================================================================
// Page
// ============================================================================

export function KernelList() {
  const navigate = useNavigate();
  const { getAllKernels, fetchFromSupabase } = useStrategyKernelStore();

  useEffect(() => {
    fetchFromSupabase();
  }, [fetchFromSupabase]);

  const kernels = getAllKernels();

  const activeCount = useMemo(
    () => kernels.filter(k => k.status === 'active').length,
    [kernels]
  );

  const renderItem = useCallback(
    (kernel: StrategyKernel) => <KernelRow key={kernel.id} kernel={kernel} />,
    []
  );

  return (
    <>
      <EntityListPage<StrategyKernel, string>
        items={kernels}
        getId={(k) => k.id}
        masterDetail={false}
        title="Kernel Strategies"
        subtitle={`${kernels.length} strateg${kernels.length !== 1 ? 'ies' : 'y'} · ${activeCount} active`}
        icon={<LightBulbIcon className="h-4 w-4 text-[var(--mustard)]" />}
        iconBgClassName="bg-[var(--mustard-dim)]"
        itemLabel="kernel strategy"
        searchFields={['name', 'diagnosis', 'guidingPolicy', 'description']}
        searchPlaceholder="Search kernel strategies…"
        sortOptions={SORT_OPTIONS}
        defaultSort={{ field: 'updatedAt', direction: 'desc' }}
        facets={FACETS}
        viewModes={['list']}
        defaultViewMode="list"
        contentClassName="overflow-hidden  bg-[var(--bg-raised)] shadow divide-y divide-[var(--rule)]"
        renderItem={renderItem}
        onItemClick={(k) => navigate(`/prioritization/strategy/kernel/${k.id}`)}
        onRefresh={() => void fetchFromSupabase()}
        emptyTitle="No kernel strategies yet"
        emptyDescription="Use the Kernel approach to build strategy as a response to a specific challenge — Diagnosis → Guiding Policy → Coherent Actions."
        primaryAction={{
          label: 'New Kernel Strategy',
          onClick: () => navigate('/prioritization/strategy/kernel/new'),
        }}
        headerActions={<StrategyConnectivityHelpButton context="kernel_list" />}
        shellClassName="h-[calc(100vh-6rem)]"
      />
    </>
  );
}

export default KernelList;
